import React from 'react';
import { useGetConfigurationProductQuery, usePostResolveConfigurationProductMutation } from '../../../store/Api';
import { Panel, PanelType, PrimaryButton, Spinner } from '@fluentui/react';
import CurrencyDisplay from '../../Currency/CurrencyDisplay';
import { IConfigurationOption, IConfigurationResolutionRequest, IConfigurationResolutionResponse, IConfigurationSetting } from '../../../models/Configurator';
import { Markdown } from 'react-showdown';
import { IsMutationSuccess } from '../../../logic/MutationTypeChecker';
import DetailsModal from '../../DetailsModal/DetailsModal';
import { INormalizedProduct } from '../../../models/NormalizedProduct';

interface IConfiguratorPanelProps {
    configId: string;
    name?: string;
    source?: INormalizedProduct;
    onClose: () => void;
}

const ConfiguratorPanel: React.FC<IConfiguratorPanelProps> = (props) => {
    const configurationProduct = useGetConfigurationProductQuery(props.configId);
    const [configurableSettings, setConfigurableSettings] = React.useState<IConfigurationSetting[]>([]);
    const [selectedSettings, setSelectedSettings] = React.useState<{[key: string]: string}>({});
    const [cost, setCost] = React.useState<number>(0);
    const [optionCosts, setOptionCosts] = React.useState<{[key: string]: {[key: string]: number}}>({});
    const [resolve] = usePostResolveConfigurationProductMutation();
    const [resolvedProduct, setResolvedProduct] = React.useState<string | undefined>(undefined);
    const [resolutionError, setResolutionError] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        const costs: {[key: string]: {[key: string]: number}} = {};
        if (configurationProduct.data?.settings) {
            const initialSettings: {[key: string]: string} = {};
            configurationProduct.data.settings.forEach((setting) => {
                const defaultOption = setting.options?.find((option) => option.isDefault);
                if (props.source?.facets?.[setting.id] && setting.options?.find((option) => option.id === props.source!.facets[setting.id])) {
                    initialSettings[setting.id] = props.source.facets[setting.id];
                } else if (defaultOption) {
                    initialSettings[setting.id] = defaultOption.id;
                } else if (setting.options?.length) {
                    initialSettings[setting.id] = sortOptions(setting.options)[0].id;
                }
                for (const option of setting.options ?? []) {
                    if (!costs[setting.id]) {
                        costs[setting.id] = {};
                    }
                    costs[setting.id][option.id] = option.cost ?? 0;
                }
            });
            // If sort order is set on any of them, sort by that. Otherwise, sort alphabetically
            setConfigurableSettings(sortSettings(configurationProduct.data.settings ?? []));
            setSelectedSettings(initialSettings);
            setOptionCosts(costs);
        }
    }, [configurationProduct.data?.settings]);

    React.useEffect(() => {
        let newCost = configurationProduct.data?.baseCost ?? 0;
        for(const setting of configurationProduct.data?.settings ?? []) {
            const selectedOption = setting.options?.find((option) => option.id === selectedSettings[setting.id]);
            if (selectedOption) {
                newCost += selectedOption.cost ?? 0;
            }
        }
        setCost(newCost);
    }, [selectedSettings, configurationProduct.data?.settings])

    const sortSettings = (settings: IConfigurationSetting[]) => {
        if (settings.some((setting) => setting.sortOrder !== undefined)) {
            return [...settings].sort((a, b) => (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000));
        }
        return [...settings].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    };

    const sortOptions = (options: IConfigurationOption[]) => {
        if (options.some((option) => option.sortOrder !== undefined)) {
            return [...options].sort((a, b) => (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000));
        }
        return [...options].sort((a, b) => (a.cost ?? 0) - (b.cost ?? 0));
    }

    const handleOpenProduct = async () => {
        const request: IConfigurationResolutionRequest = {
            configurationSettings: selectedSettings,
        };
        const result = await resolve({id: props.configId, req: request});
        if (IsMutationSuccess<IConfigurationResolutionResponse>(result)) {
            if (result.data.productId) {
                setResolvedProduct(result.data.productId);
            } else {
                setResolutionError(result.data.errorMessage ?? 'No product found');
            }
        } else {
            setResolutionError('Error resolving product');
        }
    }

    return (<>
        <Panel
            isOpen={true}
            onDismiss={props.onClose}
            headerText={props.name ?? configurationProduct.data?.name}
            isBlocking={false}
            closeButtonAriaLabel="Close"
            isLightDismiss={true}
            isFooterAtBottom={true}
            type={PanelType.medium}
            styles={{
                footer: { backgroundColor: 'white' },
            }}
            onRenderFooterContent={() => (
                <div>
                    {configurationProduct.data && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                            <span style={{ fontWeight: 'bold' }}>Total Cost:</span>
                            <span><CurrencyDisplay value={cost} /></span>
                        </div>
                    )}
                    <PrimaryButton
                        disabled={configurationProduct.isLoading}
                        text='Open Product'
                        style={{ width: '100%', margin: '10px 0' }}
                        onClick={handleOpenProduct}
                        />
                </div>
            )}
            >
                {configurationProduct.data && (<>
                {configurationProduct.data?.description && (
                    <div style={{ padding: '10px' }}>
                        <Markdown
                            options={{
                                tables: true,
                                strikethrough: true,
                                tasklists: true,
                                emoji: true,
                            }}
                            markdown={configurationProduct.data.description}
                            style={{ margin: '0 auto', maxWidth: '600px' }}
                        />
                    </div>
                )}
                {configurationProduct.data?.settings?.length && (
                    <div>
                        {configurableSettings.map((setting) => (
                            <div key={setting.id} style={{ borderBottom: '1px solid #ccc', paddingBottom: '2em' }}>
                                <h3 style={{marginTop: '1em'}}>{setting.name}</h3>
                                <p>{setting.description}</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
                                    {sortOptions(setting.options ?? []).map((option) => (
                                        <div
                                            key={option.id}
                                            style={{
                                                border: selectedSettings[setting.id] === option.id ? '2px solid rgb(120, 156, 47)' : '1px solid #ccc',
                                                backgroundColor: selectedSettings[setting.id] === option.id ? 'rgb(120, 156, 47, 0.1)' : 'white',
                                                padding: '10px',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                width: '150px',
                                            }}
                                            onClick={() => {
                                                setSelectedSettings((prev) => ({
                                                    ...prev,
                                                    [setting.id]: option.id,
                                                }));
                                            }}
                                        >
                                            <div>
                                                <h4 style={{margin: 0}}>{option.name}</h4>
                                                {option.description && <p>{option.description}</p>}
                                            </div>
                                            <div>
                                                {selectedSettings[setting.id] === option.id && (<span>
                                                    {option.cost ? 'Selected' : 'Included'}
                                                </span>)}
                                                {selectedSettings[setting.id] !== option.id && (<span>
                                                    <CurrencyDisplay
                                                        showPlus
                                                        value={(option.cost ?? 0) - (optionCosts[setting.id]?.[selectedSettings[setting.id]] ?? 0)}
                                                        />
                                                </span>)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                </>)}
                {configurationProduct.isLoading && (
                            <Spinner label="Loading..." ariaLive="assertive" style={{margin: '5em'}}/>
                )}
            </Panel>
            {resolvedProduct && <DetailsModal
                product={resolvedProduct}
                onClose={() => setResolvedProduct(undefined)}
                catalog={'technology'}
                open={true}
                />}
    </>);
}

export default ConfiguratorPanel;